import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { authAPI } from '../services/api'
import { REFRESH_INTERVAL_MS, shouldRefreshOnFocus } from '../hooks/authRefreshLogic.mjs'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('iewrs_token'))

  // RBAC Slice 3 — when `user` (and therefore its `permissions` array every
  // hasAnyPermission()/hasPermission() gate reads) was last confirmed live
  // from the server. Feeds the focus-refresh throttle below; a ref, not
  // state, because updating it must never itself trigger a re-render.
  const lastRefreshedAtRef = useRef(null)

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await authAPI.getProfile()
          setUser(response.user)
          lastRefreshedAtRef.current = Date.now()
        } catch (error) {
          console.error('Auth initialization failed:', error)
          localStorage.removeItem('iewrs_token')
          setToken(null)
        }
      }
      setLoading(false)
    }

    initAuth()
  }, [token])

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password)
      const { accessToken: newToken, user: userData } = response

      localStorage.setItem('iewrs_token', newToken)
      setToken(newToken)
      setUser(userData)
      lastRefreshedAtRef.current = Date.now()

      return { success: true, user: userData }
    } catch (error) {
      // Distinguish a down server from a wrong password rather than
      // collapsing both to a generic 'Login failed'.
      const data = error.response?.data
      const message =
        data?.error ||
        data?.message ||
        (error.response
          ? `Login failed (HTTP ${error.response.status})`
          : 'Could not reach the IEWRS server. Check your connection and try again.')

      return { success: false, error: message }
    }
  }

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData)
      return { success: true, data: response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      }
    }
  }

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout()
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('iewrs_token')
      setToken(null)
      setUser(null)
    }
  }

  // Re-fetches /api/auth/me and updates the cached user object. Used after
  // a self-service profile save (AccountPage) so the header/sidebar reflect
  // the new name immediately instead of showing stale data until the next
  // login — a save that "succeeds" but leaves the visible UI wrong is its
  // own quieter version of the deceptive-success bug this page used to have.
  const refreshUser = async () => {
    if (!token) return null
    try {
      const response = await authAPI.getProfile()
      setUser(response.user)
      lastRefreshedAtRef.current = Date.now()
      return response.user
    } catch (error) {
      console.error('User refresh failed:', error)
      return null
    }
  }

  // RBAC Slice 3 — keeps the cached `user` (and every permission gate fed by
  // it) in sync with the server's live effective permissions WITHOUT
  // requiring re-login: a background poll (catches a change even in a tab
  // that's never blurred/refocused) plus a throttled refetch whenever the
  // tab regains focus/visibility (the common case — an admin edits this
  // user's role/permissions in another tab or session). Timing decisions
  // live in hooks/authRefreshLogic.mjs so they're covered by node --test;
  // this effect is just the DOM wiring around them.
  useEffect(() => {
    if (!token) return undefined

    const maybeRefreshOnFocus = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return
      if (shouldRefreshOnFocus(lastRefreshedAtRef.current, Date.now())) {
        refreshUser()
      }
    }

    const intervalId = setInterval(() => {
      refreshUser()
    }, REFRESH_INTERVAL_MS)

    window.addEventListener('focus', maybeRefreshOnFocus)
    document.addEventListener('visibilitychange', maybeRefreshOnFocus)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', maybeRefreshOnFocus)
      document.removeEventListener('visibilitychange', maybeRefreshOnFocus)
    }
    // Deliberately only [token]: refreshUser()/shouldRefreshOnFocus close
    // over lastRefreshedAtRef (a ref, always current) and the current
    // token via the outer closure recreated whenever this effect reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const refreshToken = async () => {
    try {
      const response = await authAPI.refreshToken()
      const newToken = response.accessToken || response.token

      if (!newToken) {
        throw new Error('No token in refresh response')
      }

      localStorage.setItem('iewrs_token', newToken)
      setToken(newToken)

      return newToken
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      return null
    }
  }

  const hasPermission = (permission) => {
    if (!user) return false
    // super_admin can do everything by definition — bypass the (possibly
    // stale, JWT-baked) permissions array so route/feature gates stay
    // consistent with the Sidebar's hasAnyPermission (utils/permissions.js).
    if (user.role === 'super_admin' || (user.roles || []).includes('super_admin')) return true
    if (!user.permissions) return false
    return user.permissions.includes(permission)
  }

  const hasRole = (role) => {
    if (!user || !user.roles) return false
    return user.roles.includes(role)
  }

  const isAuthenticated = !!user && !!token

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
    hasPermission,
    hasRole
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}